import { ChangeEvent, useState } from 'react';
import intl from 'react-intl-universal';
import {
  Dropdown,
  FormItem,
  TextArea,
  TextField,
  Typography,
} from '@getsynapse/design-system';
import {
  FetchedEvent,
  ScheduleProjectOption,
} from 'utils/types/learningSchedule';
import { getEventTypesOptions } from 'Pages/LearningSchedule/constants/constants';
import { Option } from 'utils/customTypes';
import ProjectsDropdown from './ProjectsDropdown/ProjectsDropdown';
import { LONG_INPUTS_LENGTH } from 'utils/constants';

type DetailsSectionProps = {
  changeEventData: (value: any, prop: string) => void;
  projectIds: string[];
  existingEventData?: FetchedEvent;
  projects: ScheduleProjectOption[];
  fmEventDescription?: boolean;
};

const DetailsSection = ({
  existingEventData,
  changeEventData,
  projectIds,
  projects,
  fmEventDescription = false,
}: DetailsSectionProps) => {
  const [eventTitle, setEventTitle] = useState(
    existingEventData?.subject || ''
  );
  const [eventDescription, setEventDescription] = useState(
    existingEventData?.description ?? ''
  );
  const EventTypesOptions = getEventTypesOptions();
  const existingEventTypeOption = EventTypesOptions.find(
    (option) => option.value === existingEventData?.learningEventType
  );
  const typeValues = [];
  const setProjectsIds = (options: Option[]) => {
    changeEventData(
      options.map((option) => option.value),
      'projectIds'
    );
  };

  if (existingEventTypeOption) {
    typeValues.push({
      label: existingEventTypeOption.label,
      value: existingEventTypeOption.value,
    });
  }

  return (
    <div data-testid='add-event-modal-detail'>
      <Typography variant='body2' className='mt-2'>
        {intl.get('SCHEDULE.EVENTS.DETAILS_SECTION.DETAILS_TITLE')}
      </Typography>
      <FormItem
        label={intl.get('SCHEDULE.EVENTS.DETAILS_SECTION.TITLE_LABEL')}
        className='mt-4'
        labelProps={{
          required: true,
        }}
      >
        <TextField
          defaultValue={eventTitle}
          maxLength={LONG_INPUTS_LENGTH}
          placeholder={intl.get(
            'SCHEDULE.EVENTS.DETAILS_SECTION.TITLE_PLACEHOLDER'
          )}
          data-testid='event_title__textfield'
          displayCharactersCount
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setEventTitle(event.target.value);
            changeEventData(event.target.value, 'eventTitle');
          }}
        />
      </FormItem>
      <FormItem
        label={intl.get('SCHEDULE.EVENTS.DETAILS_SECTION.TYPE_LABEL')}
        className='mt-4'
        labelProps={{ required: true }}
      >
        <Dropdown
          values={typeValues}
          options={EventTypesOptions}
          onChange={(option: Option) => {
            changeEventData(option.value, 'learningEventTypeId');
          }}
          triggerProps={{
            'data-testid': 'event_types__trigger',
          }}
          placeholder={intl.get(
            'SCHEDULE.EVENTS.DETAILS_SECTION.TYPE_PLACEHOLDER'
          )}
        />
      </FormItem>
      <FormItem
        label={intl.get('SCHEDULE.EVENTS.DETAILS_SECTION.PROJECT_LABEL')}
        className='mt-4'
      >
        <ProjectsDropdown
          onChange={setProjectsIds}
          value={projectIds}
          projectsData={projects}
        />
      </FormItem>
      {fmEventDescription && (
        <FormItem
          label={intl.get('SCHEDULE.EVENTS.DETAILS_SECTION.DESCRIPTION_LABEL')}
          className='mt-4'
          data-testid='event_description__textarea'
        >
          <TextArea
            textAreaProps={{
              placeholder: intl.get(
                'SCHEDULE.EVENTS.DETAILS_SECTION.DESCRIPTION_PLACEHOLDER'
              ),
              className: 'h-36 overflow-y-auto',
            }}
            value={eventDescription}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
              setEventDescription(event.target.value);
              changeEventData(event.target.value, 'description');
            }}
          />
        </FormItem>
      )}
      <Typography variant='body2' className='mt-12 pb-4'>
        {intl.get('SCHEDULE.EVENTS.EVENT_SCHEDULE_SECTION.SCHEDULE_TITLE')}
      </Typography>
    </div>
  );
};

export default DetailsSection;
